import type { JSX } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

import { Button, Tag } from '../ds';
import { friendlyError } from '../../lib/api';
import { OPENING_TASKS_KEYS, openingTasksApi, type MomentDto, type RoomThreadDto } from '../../lib/api/openingTasks';
import { fmtWhen } from './utils';

/**
 * «Моменты» помещения — что куратор попросил исправить. Партнёр отмечает
 * «Сделано» (с необязательной заметкой), куратор подтверждает; пока не
 * подтверждено — отметку можно снять.
 */
export function MomentsList({
  roomId,
  moments,
  onShowMessage,
}: {
  roomId: string;
  moments: MomentDto[];
  onShowMessage: (commentId: string) => void;
}): JSX.Element {
  if (!moments.length) {
    return <div className="fk-ot-note">Моментов нет. Если куратор попросит что-то исправить по помещению, это появится здесь.</div>;
  }
  const open = moments.filter((m) => m.status === 'open');
  const done = moments.filter((m) => m.status === 'done');
  const confirmed = moments.filter((m) => m.status === 'confirmed');
  const groups: Array<[string, MomentDto[]]> = [
    ['Нужно исправить', open],
    ['Сделано — ждёт подтверждения куратора', done],
    ['Подтверждено куратором', confirmed],
  ];
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {groups
        .filter(([, list]) => list.length > 0)
        .map(([title, list]) => (
          <div key={title}>
            <div className="fk-ot-mgroup">
              {title} · {list.length}
            </div>
            {list.map((m) => (
              <MomentRow key={m.id} roomId={roomId} m={m} onShowMessage={onShowMessage} />
            ))}
          </div>
        ))}
    </div>
  );
}

function MomentRow({ roomId, m, onShowMessage }: { roomId: string; m: MomentDto; onShowMessage: (commentId: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [formOpen, setFormOpen] = useState(false);
  const [note, setNote] = useState('');

  const applyMoment = (upd: MomentDto): void => {
    qc.setQueryData<RoomThreadDto>(OPENING_TASKS_KEYS.thread(roomId), (prev) =>
      prev ? { ...prev, moments: prev.moments.map((x) => (x.id === upd.id ? upd : x)) } : prev,
    );
    // Системное сообщение в переписке и счётчики помещения — с сервера.
    void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.thread(roomId) });
    void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
  };

  const markDone = useMutation({
    mutationFn: () => openingTasksApi.momentDone(m.id, note.trim() || undefined),
    onSuccess: (upd) => {
      applyMoment(upd);
      setFormOpen(false);
      setNote('');
      message.success('Отмечено — куратор проверит и подтвердит');
    },
    onError: (e) => message.error(friendlyError(e)),
  });
  const undo = useMutation({
    mutationFn: () => openingTasksApi.momentUndo(m.id),
    onSuccess: (upd) => applyMoment(upd),
    onError: (e) => message.error(friendlyError(e)),
  });

  const toMessage = m.commentId ? (
    <button type="button" className="fk-ot-linkbtn" onClick={() => onShowMessage(m.commentId!)}>
      к сообщению в переписке
    </button>
  ) : null;

  if (m.status === 'confirmed') {
    return (
      <div className="fk-ot-mrow" data-status="confirmed">
        <Tag color="green">Готово</Tag>
        <div className="fk-ot-mtext">
          {m.text}
          <small>
            подтвердил {m.confirmedBy ?? 'куратор'}
            {m.confirmedAt ? ` · ${fmtWhen(m.confirmedAt)}` : ''}
          </small>
          {m.doneNote ? <small>ваша заметка: {m.doneNote}</small> : null}
        </div>
        <span />
      </div>
    );
  }

  if (m.status === 'done') {
    return (
      <div className="fk-ot-mrow" data-status="done">
        <Tag color="gold">Ждёт куратора</Tag>
        <div className="fk-ot-mtext">
          {m.text}
          <small>
            отметил {m.doneBy ?? 'партнёр'}
            {m.doneAt ? ` · ${fmtWhen(m.doneAt)}` : ''}
          </small>
          {m.doneNote ? <small>заметка: {m.doneNote}</small> : null}
          {toMessage}
        </div>
        <button type="button" className="fk-ot-linkbtn" disabled={undo.isPending} onClick={() => undo.mutate()}>
          {undo.isPending ? 'Отменяем…' : 'Отменить отметку'}
        </button>
      </div>
    );
  }

  return (
    <div className="fk-ot-mrow" data-status="open">
      <Tag color="orange">Исправить</Tag>
      <div className="fk-ot-mtext">
        {m.text}
        <small>
          поставил {m.createdBy ?? 'куратор'} · {fmtWhen(m.createdAt)}
        </small>
        {toMessage}
      </div>
      {formOpen ? (
        <span />
      ) : (
        <Button variant="ghost" size="sm" icon={<CheckOutlined />} onClick={() => setFormOpen(true)}>
          Сделано
        </Button>
      )}
      {formOpen ? (
        <div className="fk-ot-mform">
          <textarea
            className="fk-ot-textarea"
            value={note}
            maxLength={2000}
            placeholder="Что сделали (необязательно). Фото и видео лучше приложить в переписке"
            aria-label="Заметка к моменту"
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="fk-ot-req-row">
            <Button variant="primary" size="sm" icon={<CheckOutlined />} loading={markDone.isPending} onClick={() => markDone.mutate()}>
              Отметить сделанным
            </Button>
            <Button variant="text" size="sm" disabled={markDone.isPending} onClick={() => setFormOpen(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
